import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Plus, QrCode, Search } from "lucide-react";

import DataTable from "../../components/data-table/DataTable";
import EmptyState from "../../components/data-table/EmptyState";
import DeexPending from "../../components/DeexPending";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import GetRates from "../../components/utils/GetRates";
import { APP_BUSINESS_URL } from "../../constant";
import { API } from "../../constant/api";
import { useBusiness } from "../../hooks/use-business";
import { useBusinesses } from "../../hooks/use-businesses";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import AddBanks from "../banks/AddBanks";
import CreatePaymentDialog from "./components/CreatePaymentDialog";
import PaymentDetailSheet from "./components/PaymentDetailSheet";
import PaymentSuccessDialog from "./components/PaymentSuccessDialog";
import BusinessLinkDialog from "./components/BusinessLinkDialog";
import { buildPaymentColumns, PaymentPageRow } from "./components/paymentColumns";

const Payment = () => {
  const navigate = useNavigate();
  const axiosInstance = useAuthAxios();
  const rate = GetRates("USDT");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openAddBank, setOpenAddBank] = useState(false);
  const [createdReference, setCreatedReference] = useState<string | null>(null);
  const [selected, setSelected] = useState<PaymentPageRow | null>(null);
  const [editing, setEditing] = useState<PaymentPageRow | null>(null);
  const [openBusinessLink, setOpenBusinessLink] = useState(false);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    businesses,
    businessId,
    error: businessesError,
    isLoading: loadingBusinesses,
  } = useBusinesses();

  // Shared with Settings via the ["get-business"] key, so a bank added anywhere
  // (including the picker inside the create/edit dialog) refreshes here too.
  const { banks } = useBusiness();

  const {
    data: payments,
    error: paymentsError,
    isLoading,
    isFetching,
  } = useQuery({
    // page and search belong in the key — without them React Query serves the
    // cached first page and pagination silently does nothing.
    queryKey: ["payment-pages", businessId, page, debouncedSearch],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getPaymentPages(businessId!, page, debouncedSearch)
      );
      return response?.data;
    },
    retry: 1,
    enabled: !!businessId,
  });

  useEffect(() => {
    const error = (businessesError ?? paymentsError) as any;
    if (error) {
      toast.error(errorMessage(error?.message || error?.data?.message));
    }
  }, [businessesError, paymentsError]);

  // The endpoint has no sort param, so ordering is whatever the backend
  // returns. Sort newest-first here — note this only orders the current page;
  // true global ordering needs a `sort` param on getPaymentPages.
  const rows: PaymentPageRow[] = useMemo(() => {
    const pages: PaymentPageRow[] = payments?.data?.pages ?? [];
    return [...pages].sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime()
    );
  }, [payments]);

  // This endpoint's meta only reports totalPages. Deriving a row count from
  // totalPages * pageSize would over-report on the last page, so the footer
  // shows page position only.
  const totalPages = payments?.data?.meta?.totalPages ?? 0;

  const businessUrl = `${APP_BUSINESS_URL}/${businessId ?? ""}`;

  const openTransactions = (row: PaymentPageRow) =>
    navigate(`/payment/${row.reference}`, { state: { name: row.name } });

  // A payout bank must exist before a page can be edited to point at one.
  const openEdit = (row: PaymentPageRow) => {
    if (banks?.length >= 1) {
      setEditing(row);
    } else {
      setOpenAddBank(true);
    }
  };

  const columns = useMemo(
    () =>
      buildPaymentColumns({
        rate,
        onViewTransactions: openTransactions,
        onShowQr: (row) => setSelected(row),
        onEdit: openEdit,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rate, banks]
  );

  const handleCreateClick = () => {
    // A payout bank is required before a page can collect money.
    if (banks?.length >= 1) {
      setOpenCreate(true);
    } else {
      setOpenAddBank(true);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] font-manrope">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="font-sora text-[24px] font-bold text-foreground">
            Payments
          </h1>
          <p className="mt-1 text-[14px] font-medium text-muted-foreground">
            Create payment pages and track what your customers send.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden min-w-0 max-w-[220px] truncate rounded-xl border bg-background px-3 py-2.5 text-[13px] font-medium text-muted-foreground mds:block">
            {businessUrl}
          </div>
          <Button
            variant="secondary"
            onClick={() => setOpenBusinessLink(true)}
            className="gap-2 bg-accent text-primary hover:bg-accent/70"
          >
            <QrCode className="size-4" />
            Business link
          </Button>
        </div>
      </div>

      <div className="mt-8">
        <DataTable<PaymentPageRow>
          columns={columns}
          data={rows}
          isLoading={isLoading || loadingBusinesses}
          isFetching={isFetching}
          onRowClick={(row) => setSelected(row)}
          toolbar={
            <>
              <div className="relative w-full mds:max-w-[280px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search payment pages"
                  className="h-9 rounded-lg pl-9"
                />
              </div>
              <Button onClick={handleCreateClick} className="h-9 gap-2 mds:ml-auto">
                <Plus className="size-4" />
                Create new page
              </Button>
            </>
          }
          emptyState={
            <EmptyState
              title={debouncedSearch ? "No matching pages" : "No pages created yet"}
              description={
                debouncedSearch
                  ? `Nothing matched "${debouncedSearch}". Try a different search.`
                  : "Every payment page you create will appear here."
              }
              action={
                !debouncedSearch ? (
                  <Button onClick={handleCreateClick} className="mt-2 gap-2">
                    <Plus className="size-4" />
                    Create your first page
                  </Button>
                ) : undefined
              }
            />
          }
          pagination={{
            page,
            totalPages,
            onPageChange: setPage,
          }}
        />
      </div>

      <PaymentDetailSheet
        page={selected}
        open={!!selected}
        onOpenChange={(next) => !next && setSelected(null)}
        rate={rate}
        onViewTransactions={(row) => {
          setSelected(null);
          openTransactions(row);
        }}
        onEdit={(row) => {
          setSelected(null);
          openEdit(row);
        }}
      />

      {businessId && (
        <CreatePaymentDialog
          open={openCreate}
          onOpenChange={setOpenCreate}
          businessId={businessId}
          banks={banks ?? []}
          onCreated={setCreatedReference}
        />
      )}

      {businessId && editing && (
        <CreatePaymentDialog
          open={!!editing}
          onOpenChange={(next) => !next && setEditing(null)}
          businessId={businessId}
          banks={banks ?? []}
          page={editing}
          onCreated={setCreatedReference}
          onUpdated={() => setEditing(null)}
        />
      )}

      <PaymentSuccessDialog
        reference={createdReference}
        onOpenChange={(next) => !next && setCreatedReference(null)}
      />

      <BusinessLinkDialog
        open={openBusinessLink}
        onOpenChange={setOpenBusinessLink}
        businessUrl={businessUrl}
      />

      {openAddBank && businessId && (
        <AddBanks setOpenAddBank={setOpenAddBank} businessID={businessId} />
      )}

      {!loadingBusinesses && (!businesses || businesses.length < 1) ? (
        <DeexPending />
      ) : null}
    </div>
  );
};

export default Payment;
